// Adaptador: adminVideos.ts → consume del motor contentMaster.ts
// Exporta las mismas interfaces y datos que esperan las páginas.

import {
  canonicalVideos,
  getPhotoUrl,
  type CanonicalVideo,
} from './contentMaster';

export type VideoCategory = 'programas' | 'entrevistas' | 'eventos' | 'shorts' | 'promociones';
export type VideoStatus = 'publicado' | 'borrador' | 'programado';
export type VideoSourceType = 'upload' | 'youtube' | 'facebook';

export interface AdminVideo {
  id: number;
  title: string;
  description: string;
  category: VideoCategory;
  thumbnailUrl: string;
  sourceType: VideoSourceType;
  sourceUrl: string;
  duration: string;
  status: VideoStatus;
  isFeatured: boolean;
  fileSize: string;
  uploadedAt: string;
  scheduledAt?: string;
  // Live lineage
  derivedFromLiveId?: number;
}

export interface AdminVideoData {
  videos: AdminVideo[];
  storage: {
    used: string;
    total: string;
    percentage: number;
    usedGB: number;
    totalGB: number;
    videoCount: number;
  };
}

export const categoryLabels: Record<VideoCategory, string> = {
  programas: 'Programas',
  entrevistas: 'Entrevistas',
  eventos: 'Eventos',
  shorts: 'Shorts',
  promociones: 'Promociones',
};

export const categoryIcons: Record<VideoCategory, string> = {
  programas: 'ri-tv-line',
  entrevistas: 'ri-chat-voice-line',
  eventos: 'ri-calendar-event-line',
  shorts: 'ri-smartphone-line',
  promociones: 'ri-megaphone-line',
};

export const statusLabels: Record<VideoStatus, string> = {
  publicado: 'Publicado',
  borrador: 'Borrador',
  programado: 'Programado',
};

export const sourceTypeLabels: Record<VideoSourceType, string> = {
  upload: 'Subido',
  youtube: 'YouTube',
  facebook: 'Facebook',
};

export const sourceTypeIcons: Record<VideoSourceType, string> = {
  upload: 'ri-upload-cloud-2-line',
  youtube: 'ri-youtube-line',
  facebook: 'ri-facebook-circle-line',
};

// ─── Mapeo de canonical a legacy ──────────────────────────

function toLegacyVideoId(canonicalId: string): number {
  return parseInt(canonicalId.split('-')[1], 10);
}

function mapStatus(s: CanonicalVideo['status']): VideoStatus {
  if (s === 'published') return 'publicado';
  if (s === 'draft') return 'borrador';
  return 'programado';
}

function mapVideo(v: CanonicalVideo): AdminVideo {
  return {
    id: toLegacyVideoId(v.id),
    title: v.title,
    description: v.description,
    category: v.category as VideoCategory,
    thumbnailUrl: getPhotoUrl(v.thumbnailPhotoId),
    sourceType: v.sourceType as VideoSourceType,
    sourceUrl: v.sourceUrl,
    duration: v.duration,
    status: mapStatus(v.status),
    isFeatured: v.isFeatured,
    fileSize: v.fileSize,
    uploadedAt: v.createdAt,
    scheduledAt: v.scheduledAt,
    derivedFromLiveId: v.derivedFromLiveId ? toLegacyVideoId(v.derivedFromLiveId) : undefined,
  };
}

const adaptedVideos: AdminVideo[] = canonicalVideos.map(mapVideo);

export const adminVideoData: AdminVideoData = {
  storage: {
    used: '34.8 GB',
    total: '100 GB',
    percentage: 35,
    usedGB: 34.8,
    totalGB: 100,
    videoCount: adaptedVideos.length,
  },
  videos: adaptedVideos,
};